import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { useSession } from "@/hooks/useSession";
import LockScreen from "@/components/LockScreen";

// Pages
import Dashboard from "@/pages/Dashboard";
import ClientesList from "@/pages/ClientesList";
import ClienteForm from "@/pages/ClienteForm";
import ConsultaCadastro from "@/pages/ConsultaCadastro";
import ClienteDetalhes from "@/pages/ClienteDetalhes";
import MonitoramentoContratos from "@/pages/MonitoramentoContratos";
import CadastroContrato from "@/pages/CadastroContrato";
import Bancos from "@/pages/Bancos";
import BancoEditar from "@/pages/BancoEditar";
import MotorCredito from "@/pages/MotorCredito";
import Financeiro from "@/pages/Financeiro";
import Vendas from "@/pages/Vendas";
import Orgaos from "@/pages/Orgaos";
import Relatorios from "@/pages/Relatorios";
import RelatorioAprovados from "@/pages/RelatorioAprovados";
import RelatorioComissoesPendentes from "@/pages/RelatorioComissoesPendentes";
import VincularComissoesRecebidas from "@/pages/VincularComissoesRecebidas";
import GerarComissoesPagamento from "@/pages/GerarComissoesPagamento";
import GerenciadorTabelasComissao from "@/pages/GerenciadorTabelasComissao";
import Layout from "@/components/Layout";

// Gestão de Acesso
import Login from "@/pages/Login";
import GestaoUsuarios from "@/pages/GestaoUsuarios";
import GestaoEmpresas from "@/pages/GestaoEmpresas";
import GestaoFuncoes from "@/pages/GestaoFuncoes";
import PlanoMetas from "@/pages/PlanoMetas";
import DashboardLeads from "@/pages/DashboardLeads";
import DashboardLeadsBase from "@/pages/DashboardLeadsBase";
import Simulador from "@/pages/Simulador";
import AtendimentoWhatsApp from "@/pages/AtendimentoWhatsApp";
import GestaoConexoesWhatsApp from "@/pages/GestaoConexoesWhatsApp";
import GestaoTelefones from "@/pages/GestaoTelefones";
import CentralVoIP from "@/pages/CentralVoIP";
import SmsMarketing from "@/pages/SmsMarketing";
import ProspectaCLT from "@/pages/ProspectaCLT";
import LandingSimulacaoCLT from "@/pages/LandingSimulacaoCLT";
import LandingServidorFederal from "@/pages/LandingServidorFederal";
import ServidorFederal from "@/pages/ServidorFederal";
import RelatorioIdentificacaoUnica from "@/pages/RelatorioIdentificacaoUnica";
import ChatMobile from "@/pages/ChatMobile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Componente interno que usa o hook de sessão
function AppContent() {
  const location = useLocation();
  const { sessionLocked, usuario, unlockScreen, handleLogout } = useSession();
  
  // Não mostrar tela de bloqueio na página de login nem nas páginas públicas
  const publicPaths = ["/login", "/simular", "/simulacao-clt", "/simular_federal", "/chat"];
  const isLoginPage = publicPaths.includes(location.pathname);
  
  return (
    <>
      {/* Tela de Bloqueio */}
      {sessionLocked && !isLoginPage && (
        <LockScreen 
          usuario={usuario}
          onUnlock={unlockScreen}
          onLogout={handleLogout}
        />
      )}
      
      <Routes>
        {/* Login - Rota pública */}
        <Route path="/login" element={<Login />} />

        {/* Landing pública de simulação (e-mail marketing) */}
        <Route path="/simular" element={<LandingSimulacaoCLT />} />
        <Route path="/simulacao-clt" element={<LandingSimulacaoCLT />} />
        <Route path="/simular_federal" element={<LandingServidorFederal />} />
        <Route path="/chat" element={<ChatMobile />} />
        
        {/* Rotas protegidas pelo Layout */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Dashboard />} />
          <Route path="clientes" element={<ClientesList />} />
          <Route path="clientes/novo" element={<ClienteForm />} />
          <Route path="consultar-cadastrar" element={<ConsultaCadastro />} />
          <Route path="clientes/:id" element={<ClienteDetalhes />} />
          <Route path="clientes/:id/editar" element={<ClienteForm />} />
          <Route path="contratos" element={<MonitoramentoContratos />} />
          <Route path="monitoramento-contratos" element={<MonitoramentoContratos />} />
          <Route path="cadastro-contrato" element={<CadastroContrato />} />
          <Route path="cadastro-contrato/:id/editar" element={<CadastroContrato />} />
          <Route path="bancos" element={<Bancos />} />
          <Route path="bancos/novo" element={<BancoEditar />} />
          <Route path="bancos/:id/editar" element={<BancoEditar />} />
          <Route path="motor-credito" element={<MotorCredito />} />
          <Route path="financeiro" element={<Financeiro />} />
          <Route path="vendas" element={<Vendas />} />
          <Route path="orgaos" element={<Orgaos />} />
          <Route path="tabelas-comissao" element={<GerenciadorTabelasComissao />} />
          <Route path="relatorios" element={<Relatorios />} />
          <Route path="relatorios/identificacao-unica" element={<RelatorioIdentificacaoUnica />} />
          <Route path="relatorios/aprovados" element={<RelatorioAprovados />} />
          <Route path="relatorios/comissoes-pendentes" element={<RelatorioComissoesPendentes />} />
          <Route path="relatorios/vincular-comissoes" element={<VincularComissoesRecebidas />} />
          <Route path="relatorios/gerar-comissoes" element={<GerarComissoesPagamento />} />
          
          {/* Gestão de Acesso */}
          <Route path="gestao/usuarios" element={<GestaoUsuarios />} />
          <Route path="gestao/empresas" element={<GestaoEmpresas />} />
          <Route path="gestao/funcoes" element={<GestaoFuncoes />} />
          <Route path="metas" element={<PlanoMetas />} />
          <Route path="dashboard-leads" element={<DashboardLeads />} />
          <Route path="dashboard-leads-base" element={<DashboardLeadsBase />} />
          <Route path="simulador" element={<Simulador />} />
          <Route path="atendimento-whatsapp" element={<AtendimentoWhatsApp />} />
          <Route path="gestao-conexoes-whatsapp" element={<GestaoConexoesWhatsApp />} />
          <Route path="gestao-telefones" element={<GestaoTelefones />} />
          <Route path="central-voip" element={<CentralVoIP />} />
          <Route path="sms-marketing" element={<SmsMarketing />} />
          <Route path="prospecta-clt" element={<ProspectaCLT />} />
          <Route path="servidor-federal" element={<ServidorFederal />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
